"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { GrainGradient } from "@paper-design/shaders-react";

const heroShaderMaxPixelCount = 650000;
const heroShaderSpeed = 0.24;

const grainGradientColors = [
  "#7300ff",
  "#eba8ff",
  "#00bfff",
  "#2b00ff",
] satisfies string[];

function supportsHeroShader() {
  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    typeof window.ResizeObserver === "undefined" ||
    typeof window.visualViewport === "undefined"
  ) {
    return false;
  }

  const canvas = document.createElement("canvas");

  try {
    return canvas.getContext("webgl2") !== null;
  } catch {
    return false;
  }
}

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);

    updatePreference();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updatePreference);
    } else {
      mediaQuery.addListener(updatePreference);
    }

    return () => {
      if (typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener("change", updatePreference);
      } else {
        mediaQuery.removeListener(updatePreference);
      }
    };
  }, []);

  return prefersReducedMotion;
}

function useHeroShaderSupport() {
  return useSyncExternalStore(
    () => () => {},
    supportsHeroShader,
    () => false,
  );
}

function useIsHeroShaderVisible(
  elementRef: React.RefObject<HTMLElement | null>,
) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const element = elementRef.current;

    if (
      !element ||
      typeof window === "undefined" ||
      typeof window.IntersectionObserver === "undefined"
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry?.isIntersecting ?? true);
      },
      {
        rootMargin: "160px 0px",
        threshold: 0,
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [elementRef]);

  return isVisible;
}

export function HeroShaderBackground() {
  const containerRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const isShaderSupported = useHeroShaderSupport();
  const isShaderVisible = useIsHeroShaderVisible(containerRef);

  return (
    <div
      ref={containerRef}
      aria-hidden
      className="pointer-events-none absolute inset-x-[-18%] bottom-0 top-[-12%] z-0 overflow-hidden bg-[#050509] opacity-75 [mask-image:linear-gradient(180deg,black_0%,black_68%,rgba(0,0,0,0.72)_82%,transparent_100%)] [-webkit-mask-image:linear-gradient(180deg,black_0%,black_68%,rgba(0,0,0,0.72)_82%,transparent_100%)] sm:inset-x-[-12%] md:top-[-18%] xl:inset-x-[-8%] xl:top-[-24%]"
    >
      {isShaderSupported ? (
        <GrainGradient
          className="hero-shader-background absolute inset-[-4%] h-[108%] w-[108%] scale-105 opacity-80 saturate-[1.16]"
          colors={grainGradientColors}
          colorBack="#000000"
          fit="cover"
          intensity={0.5}
          maxPixelCount={heroShaderMaxPixelCount}
          noise={0.25}
          offsetX={0}
          offsetY={0}
          rotation={0}
          scale={1}
          shape="corners"
          softness={0.5}
          speed={prefersReducedMotion || !isShaderVisible ? 0 : heroShaderSpeed}
        />
      ) : null}

      <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_22%,rgba(255,255,255,0.18),transparent_24%),radial-gradient(circle_at_62%_12%,rgba(0,191,255,0.24),transparent_30%),radial-gradient(circle_at_78%_58%,rgba(115,0,255,0.26),transparent_34%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(5,5,9,0.88)_0%,rgba(5,5,12,0.24)_45%,rgba(5,5,10,0.78)_100%)] md:bg-[linear-gradient(90deg,rgba(5,5,9,0.9)_0%,rgba(5,5,12,0.58)_34%,rgba(5,5,12,0.14)_62%,rgba(5,5,10,0.68)_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(5,5,9,0.68)_0%,rgba(5,5,9,0.08)_42%,rgba(17,17,19,0.92)_100%)]" />
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-b from-transparent via-[#111113]/72 to-[#111113]" />
    </div>
  );
}
